import { Prompt, PromptFunction } from '../Types';
import requestClient from '../components/Utility/RequestClient';
import Store from '../Store/Store';

const resolveDemo = () => Store.getState().environment.demoInstance;

export const promptService = {
    getPromptsByPage: (
        page: number,
        itemsPerPage: number,
        additionalParams?: Record<string, any>,
        query?: string | null,
    ): Promise<{ data: { data: Prompt[]; total: number } }> => {
        return requestClient
            .get(`${resolveDemo() ? '/demo' : ''}/prompts/get`, {
                params: {
                    page,
                    itemsPerPage,
                    query,
                },
            })
            .then((response: any) => response);
    },
    getUserPromptsByPage: (
        page: number,
        itemsPerPage: number,
    ): Promise<{ data: { data: Prompt[]; total: number } }> => {
        return requestClient
            .get(`/prompts/getuserprompts`, {
                params: {
                    page,
                    itemsPerPage,
                },
            })
            .then((response: any) => response);
    },

    getAllUserPromptsPaginated: async (itemsPerPage: number = 50): Promise<Prompt[]> => {
        const firstResponse = await promptService.getUserPromptsByPage(1, itemsPerPage);
        const { data: firstData, total } = firstResponse.data;
        const totalPages = Math.ceil(total / itemsPerPage);

        if (totalPages <= 1) {
            return firstData;
        }

        const pagePromises = [];
        for (let page = 2; page <= totalPages; page++) {
            pagePromises.push(promptService.getUserPromptsByPage(page, itemsPerPage));
        }

        const responses = await Promise.all(pagePromises);
        const allData = responses.flatMap((res) => res.data.data);
        const finalData = firstData.concat(allData);

        return finalData;
    },

    setBasePrompt: async (identifier: string): Promise<any> => {
        return requestClient.post<Prompt[]>(`/prompts/setbaseprompt/${identifier}`);
    },

    proposeGlobal: async (identifier: string): Promise<any> => {
        return requestClient.post(`/prompts/proposeglobal/${identifier}`);
    },

    unproposeGlobal: async (identifier: string): Promise<any> => {
        return requestClient.post(`/prompts/unproposeglobal/${identifier}`);
    },

    makeGlobal: async (identifier: string): Promise<any> => {
        return requestClient.post<Prompt[]>(`/prompts/makeglobal/${identifier}`);
    },

    deletePrompt: async (identifier: string): Promise<any> => {
        return requestClient.delete(`/prompts/delete/${identifier}`);
    },

    getPrompts: async (): Promise<{ data: any }> => {
        return requestClient.get<any>(`${resolveDemo() ? '/demo' : ''}/prompts/get`);
    },

    getUserPrompts: async (): Promise<{ data: { data: Prompt[]; total: number } }> => {
        return requestClient.get<{ data: Prompt[]; total: number }>(`/prompts/getuserprompts`);
    },

    uploadFile: async (formData: any): Promise<any> => {
        return requestClient.post(`/prompts/uploadfile`, formData);
    },

    getPreviewChat: async (identifier: string): Promise<any> => {
        return requestClient.get(`/chat/getpreviewpromptchat/${identifier}`);
    },
    createPreviewChat: async (identifier: string): Promise<any> => {
        return requestClient.post(`/chat/create`, { prompt: identifier, isPromptPreview: true });
    },

    savePrompt: async (urlPart: string, data: any): Promise<any> => {
        return requestClient.post<Prompt>(`/prompts/${urlPart}`, data);
    },

    clearPreviewChat: async (chatId: string): Promise<any> => {
        return requestClient.post(`/chat/clearpreviewpromptchat/${chatId}`);
    },

    getPrompt: async (userPrompt: boolean, identifier: string): Promise<{ data: Prompt }> => {
        return requestClient.get(`${resolveDemo() && !userPrompt ? '/demo' : ''}/prompts/get/${identifier}`);
    },

    getPromptFunctions: async (userPrompt: boolean): Promise<{ data: PromptFunction[] }> => {
        return requestClient.get(
            userPrompt ? '/promptfunctions/getuserfunctions' : `${resolveDemo() ? '/demo' : ''}/promptfunctions/get`,
        );
    },

    getModels: async (userPrompt: boolean): Promise<{ data: any }> => {
        return requestClient.get(
            userPrompt ? '/settings/usermodels' : `${resolveDemo() ? '/demo' : ''}/settings/allmodels`,
        );
    },

    checkUploadedFiles: async (data: any): Promise<any> => {
        return requestClient.post(`/chat/checkuploadedfiles`, data);
    },
};
